// Owns the menu-test block state — the three view modes and collapses, the delivery selection,
// the shared chart mode / zoom / sort (D5, D6) and the un-collapsed section cards — seeded from
// the block's report params and re-seeded whenever any of them changes outside the block
// (sh useShelfTestState twin).
//
// The page view never reads or composes those params itself: it receives an api on mount and
// pulls the payloads it needs (the param set keyed by prefix, the export payload, the chart
// axis, the filters target) when a change is announced through onStateChange.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getTrait } from '@aytm/app/stats/lib/stat-page'
import {
  calcLabelClickTarget,
  isCustomQuestion,
  isTraitType,
  questionPositionToId
} from '@aytm/app/stats/page/components/lib/utils'

import {
  composeMenuExportSvg,
  getMenuExportSize,
  loadMenuImageBase64
} from '@aytm/app/stats/page/question-views/mn/components/menu-section/chart/lib/export'
import { calcDisplayRange } from '@aytm/app/stats/page/question-views/mn/lib/display-range'
import { calcExportData } from '@aytm/app/stats/page/question-views/mn/lib/export-params'
import {
  calcAllUnCollapsedSections,
  calcReportParamsSection,
  calcResolvedChartMode,
  calcViewModeResets,
  resolveReportParams,
  updateUnCollapsedSections
} from '@aytm/app/stats/page/question-views/mn/lib/report-params'
import {
  PRODUCT_PERCENT_CAPABLE_VIEWS,
  SECTION_PERCENT_CAPABLE_VIEWS
} from '@aytm/app/stats/page/question-views/mn/lib/viewOptions'

const NON_RANGE_CHART_MODES = ['map', 'none']

// D12: a sections row filters by its SQ{position} section value — the base label-target grammar
// minus the batch/tag handling, ported from sections/items_list.coffee onLabelClick.
const calcSectionsLabelClickTarget = ({ id, questionId, position } = {}) => {
  let resolvedId = id
  let resolvedQuestionId = questionId

  if (null != resolvedQuestionId) {
    const first = String(resolvedQuestionId).split('-')[0]
    resolvedQuestionId = Number.isNaN(Number(first)) ? first : questionPositionToId(Number(first))
  }

  if (isCustomQuestion(resolvedQuestionId || resolvedId, 'xq')) {
    resolvedId = String(resolvedId).split('_')[0]
    resolvedQuestionId = String(resolvedQuestionId).split('_')[0]
  }

  const custom = isCustomQuestion(resolvedQuestionId || resolvedId)
  const group = custom ? 'virtualQuestions' : 'questions'
  const question = (custom && resolvedQuestionId) || questionId || resolvedId

  const key = String(
    resolvedQuestionId || (Number.isNaN(Number(resolvedId)) ? resolvedId : questionPositionToId(Number(resolvedId)))
  ).toUpperCase()

  return { group, question, key, value: `SQ${position}` }
}

// The chart mode, the zoom and the sort hang off the question key and the menu block is what
// moves them; the comments block draws neither a chart nor a sortable table, so the ones its
// open-ended state settles on are inert and never reach the write.
const commentsSection = ({ cm, z, s, ...section } = {}) => section

export default function useMenuTestState({
  id,
  image,
  components,
  params,
  format,
  question,
  deliveryOptions,
  sections,
  answerVariants,
  defaultSortOrder,
  modelChartMode,
  globalChartMode,
  globalZoomLevel,
  answer,
  ignoreAmount,
  ignorePercents,
  confidenceLevels,
  labelTarget,
  onReady,
  onStateChange,
  onCommentsParamsChange,
  onCommentsWcParamsChange,
  onUpdateData
}) {
  // the cb self-heal signal of the latest seed — a seed annotation, not state (D3)
  const seedNeedsUpdate = useRef(false)

  const seed = () => {
    const { needsUpdate, ...resolved } = resolveReportParams(params, {
      deliveryOptions,
      sections,
      defaultSortOrder
    })
    seedNeedsUpdate.current = needsUpdate
    return resolved
  }

  const [state, setState] = useState(seed)

  // every input the api payloads are built from, refreshed each render so the pull is current
  const snapshot = useRef()
  snapshot.current = {
    state,
    sections,
    // what the export composite is drawn from, beyond the state and the sections
    menu: { id, image, components },
    context: {
      deliveryOptions,
      format,
      answerVariants,
      modelChartMode,
      globalChartMode,
      globalZoomLevel,
      defaultSortOrder
    },
    range: {
      question,
      format,
      answer,
      ignoreAmount,
      ignorePercents,
      confidenceLevels,
      globalChartMode,
      globalZoomLevel
    },
    labelTarget
  }

  // The loader the shell raises for a round trip it started and lowers when the answers are in.
  // Transient view state, held apart from the state the report params are written from, so
  // raising it announces nothing and a re-seed cannot drop it mid-fetch.
  const [loading, setLoading] = useState(false)

  // A landed answer ends the round trip whichever fetch delivered it. The shell's own callback is
  // not the only way in: a question the page already lists as missing data is reloaded by the page,
  // which never calls the shell back, and a self-healed delivery filter re-fetches exactly that way.
  useEffect(() => {
    if (answer && 'object' === typeof answer) setLoading(false)
  }, [answer])

  // the rendered products chart hands over { getAxisExtremes } once mounted (D11); the maths
  // below is the fallback for a chart that has not drawn yet
  const chartApiRef = useRef(null)

  // The comments block hands its api up as it mounts. This block answers for it as well as for
  // itself, so the preset and export bridges reach both through one call.
  const commentsApiRef = useRef(null)
  const onCommentsReady = useCallback(api => {
    commentsApiRef.current = api
  }, [])

  const announceRefs = useRef()
  announceRefs.current = { onCommentsParamsChange, onCommentsWcParamsChange, onUpdateData }

  // The comments block announces its own changes the way any open-ended block does; this block is
  // what writes them down. A mode it settled on that cannot read the payload it holds is a reload,
  // and the fetch is answered against the params, so it travels with the write.
  const onCommentsStateChange = useCallback((reason, options = {}) => {
    const api = commentsApiRef.current
    if (!api) return

    const reload =
      'mode' === reason ||
      ('seed' === reason && options.needsUpdate) ||
      ('state' === reason && options.configChanged) ||
      Boolean(options.resetAnswer)

    if ('seed' !== reason || options.needsUpdate) {
      announceRefs.current.onCommentsParamsChange?.(commentsSection(api.getReportParamsSection?.()), reload)
    } else if (reload) {
      announceRefs.current.onUpdateData?.()
    }
  }, [])

  const onCommentsWcParams = useCallback(wcParams => announceRefs.current.onCommentsWcParamsChange?.(wcParams), [])

  // an expanded block is the resting state, so a collapse says nothing rather than storing a value
  const onCommentsCollapsedChange = useCallback(
    collapsed => announceRefs.current.onCommentsParamsChange?.({ mncc: collapsed || null }),
    []
  )

  const api = useMemo(() => {
    const resolveDisplayRange = (chartMode, zoomLevel) => {
      const {
        question,
        format,
        answer,
        ignoreAmount,
        ignorePercents,
        confidenceLevels,
        globalChartMode,
        globalZoomLevel
      } = snapshot.current.range

      // coffee guard order: trait, then map/none, and only then the live axis
      if (getTrait() && isTraitType({ ...question, format })) return null
      if (NON_RANGE_CHART_MODES.includes(chartMode) || NON_RANGE_CHART_MODES.includes(globalChartMode)) return null

      const extremes = chartApiRef.current?.getAxisExtremes?.()
      if (null != extremes) return extremes

      return calcDisplayRange({
        chartMode,
        zoomLevel: zoomLevel || snapshot.current.state.zoomLevel || globalZoomLevel,
        globalChartMode,
        viewMode: snapshot.current.state.productViewMode,
        format,
        answer,
        ignoreAmount,
        ignorePercents,
        confidenceLevels
      })
    }

    // Composes the pptx / download image composite (D8) from the block as it stands — the base64
    // is fetched on demand and cached per image path, and the base64-only cases (menu/chart
    // component off, declared map/none, menu collapsed) are decided here, so the chart itself
    // never has to be mounted.
    const getMenuExportSvg = async () => {
      const { state, sections, menu, context, range } = snapshot.current
      const { id, image, components = {} } = menu
      if (!image?.original) return null

      const withOverlay =
        false !== components.menuBlock &&
        false !== components.chart &&
        !NON_RANGE_CHART_MODES.includes(context.modelChartMode) &&
        !NON_RANGE_CHART_MODES.includes(range.globalChartMode) &&
        !state.menuCollapsed

      return composeMenuExportSvg({
        id,
        image,
        answer: range.answer,
        sections,
        menuViewMode: state.menuViewMode,
        imgBase64: await loadMenuImageBase64(image),
        withOverlay
      })
    }

    return {
      getReportParamsSection: () => calcReportParamsSection(snapshot.current.state, snapshot.current.context),
      // the comments block is exported as a question of its own, the way an oex variant is; no
      // live consumer reads it yet
      getExportData: () => ({
        ...calcExportData(snapshot.current.state),
        chartModeResolved: calcResolvedChartMode(snapshot.current.state, snapshot.current.context),
        comments: commentsApiRef.current?.getExportData?.()
      }),
      getMenuExportSvg,
      getMenuExportSize: () => getMenuExportSize(snapshot.current.menu.image),
      update: (change = {}) => {
        if (Object.hasOwn(change, 'loading')) setLoading(Boolean(change.loading))
      },
      // preset bridge: the param the comments cloud would end up with if the preset were poured
      // into it. Resolving commits nothing -- the shell writes the section down.
      getWCParam: preset => {
        const commentsApi = commentsApiRef.current
        if (!commentsApi) return {}

        return commentsApi.getWcParamsSection?.(commentsApi.importWcPreset?.(preset)) || {}
      },
      getLabelClickTarget: (position, kind) =>
        'sections' === kind
          ? calcSectionsLabelClickTarget({ ...snapshot.current.labelTarget, position })
          : calcLabelClickTarget({ ...snapshot.current.labelTarget, position }),
      // no live consumer yet — kept sh-parity-ready for exports (D11)
      getDisplayRange: resolveDisplayRange
    }
  }, [])

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // The cb filter and the delivery stale-fallback resolve against the schema lists, so a
  // sections / deliveryOptions change re-seeds too (the coffee sub-items view re-imports on
  // change:sections) — unlike sh, whose grammar is schema-free.
  const seedKey = JSON.stringify({
    params: params ?? null,
    sections: (sections || []).map(section => section.uid ?? section.position),
    deliveryOptions: deliveryOptions ?? null
  })
  const lastSeedKey = useRef(seedKey)
  useEffect(() => {
    if (lastSeedKey.current === seedKey) return
    lastSeedKey.current = seedKey

    setState(seed())
    // seed() reads the live props, which seedKey is derived from
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey])

  // The reason tells the view what the change means: 'seed' is the resolved starting state
  // (with the cb self-heal flagged through needsUpdate, OE-style), 'state' a change the
  // researcher made — re-seeds after outside param changes announce 'state', like sh, which
  // also lets the host persist a healed cb rewrite.
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  const lastState = useRef(null)
  useEffect(() => {
    const reason = null === lastState.current ? 'seed' : 'state'
    lastState.current = state

    if ('seed' === reason) onStateChangeRef.current?.(reason, { needsUpdate: seedNeedsUpdate.current })
    else onStateChangeRef.current?.(reason)
  }, [state])

  const handlers = useMemo(
    () => ({
      onMenuModelChange: ({ viewMode } = {}) =>
        setState(current =>
          undefined === viewMode || viewMode === current.menuViewMode ? current : { ...current, menuViewMode: viewMode }
        ),
      // D6: a sections/products view switch always drops the zoom, and drops the chart mode
      // when the view being entered has no percent axis while the one being left had
      onSectionsModelChange: ({ viewMode } = {}) =>
        setState(current => {
          if (undefined === viewMode || viewMode === current.sectionsViewMode) return current

          return {
            ...current,
            sectionsViewMode: viewMode,
            ...calcViewModeResets({
              prevViewMode: current.sectionsViewMode,
              nextViewMode: viewMode,
              percentCapableViews: SECTION_PERCENT_CAPABLE_VIEWS,
              chartMode: current.chartMode
            })
          }
        }),
      onProductsModelChange: ({ viewMode } = {}) =>
        setState(current => {
          if (undefined === viewMode || viewMode === current.productViewMode) return current

          return {
            ...current,
            productViewMode: viewMode,
            ...calcViewModeResets({
              prevViewMode: current.productViewMode,
              nextViewMode: viewMode,
              percentCapableViews: PRODUCT_PERCENT_CAPABLE_VIEWS,
              chartMode: current.chartMode
            })
          }
        }),
      onMenuToggle: menuCollapsed => setState(current => ({ ...current, menuCollapsed })),
      onSectionsToggle: sectionsCollapsed => setState(current => ({ ...current, sectionsCollapsed })),
      onProductsToggle: productsCollapsed => setState(current => ({ ...current, productsCollapsed })),
      onDeliveryChange: (selectedDeliveryOptions = []) =>
        setState(current => ({ ...current, selectedDeliveryOptions })),
      onSortChange: sortOrder =>
        setState(current => (current.sortOrder === sortOrder ? current : { ...current, sortOrder })),
      onChartModeChange: chartMode =>
        setState(current => (current.chartMode === chartMode ? current : { ...current, chartMode })),
      onZoomChange: (key, zoomLevel) => setState(current => ({ ...current, zoomLevel })),
      onSectionCardToggle: (position, collapsed) =>
        setState(current => ({
          ...current,
          unCollapsedSections: updateUnCollapsedSections(
            current.unCollapsedSections,
            snapshot.current.sections,
            position,
            collapsed
          )
        })),
      onSectionCardsToggleAll: collapse =>
        setState(current => ({
          ...current,
          unCollapsedSections: collapse ? [] : calcAllUnCollapsedSections(snapshot.current.sections)
        })),
      onProductsChartReady: chartApi => {
        chartApiRef.current = chartApi
      }
    }),
    []
  )

  return {
    ...state,
    loading,
    ...handlers,
    onCommentsReady,
    onCommentsStateChange,
    onCommentsWcParams,
    onCommentsCollapsedChange
  }
}
